/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Logo from '@/components/Logo';
import { Mail, ArrowLeft, MailCheck, ExternalLink, Copy, Info } from 'lucide-react';
import { forgotPassword } from '@/lib/passwordReset';

/**
 * Forgot Password entry page. User submits email, we call the backend which
 * ALWAYS returns a generic success message (enumeration protection).
 * In dev mode (EMAIL_DEV_MODE=true) the backend also returns a `dev_reset_url`
 * which we render for easy testing.
 */
const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devUrl, setDevUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await forgotPassword(email.trim());
      setSubmitted(true);
      if (r.dev_reset_url) setDevUrl(r.dev_reset_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('genericError'));
    } finally { setLoading(false); }
  };

  const copyDev = () => {
    if (!devUrl) return;
    navigator.clipboard.writeText(devUrl);
    toast.success(t('pwResetDevCopied'));
  };

  return (
    <div className="min-h-screen bg-background bg-hero-gradient flex items-center justify-center px-4 py-8 relative" data-testid="forgot-password-page">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }} className="w-full max-w-md relative">
        <div className="flex items-center justify-between mb-6">
          <Logo size={44} withText textClassName="text-base" tagline={t('tagline')} />
        </div>
        <Card className="p-6 rounded-2xl shadow-elev-lg">
          {!submitted ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-5 w-5 text-primary" />
                <h1 className="font-display font-semibold text-xl">{t('pwResetTitle')}</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-5">{t('pwResetSubtitle')}</p>
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="fp-email" className="text-xs text-muted-foreground">{t('email')}</Label>
                  <div className="mt-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="mario.rossi@esempio.it" data-testid="forgot-email-input" required autoFocus />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading || !email} data-testid="forgot-submit-btn">
                  {loading ? t('loading') : t('pwResetSendLink')}
                </Button>
              </form>
              <button onClick={() => navigate('/login')} className="mt-4 w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1" data-testid="forgot-back-btn">
                <ArrowLeft className="h-3.5 w-3.5" /> {t('pwResetBackToLogin')}
              </button>
            </>
          ) : (
            <div className="text-center py-2" data-testid="forgot-submitted-view">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
                <MailCheck className="h-7 w-7" />
              </div>
              <h2 className="mt-3 font-display font-semibold text-xl">{t('pwResetEmailSent')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('pwResetEmailSentDesc')}</p>

              {devUrl && (
                <div className="mt-5 text-left rounded-2xl border border-warning/40 bg-warning/5 p-3" data-testid="forgot-dev-notice">
                  <div className="flex items-start gap-2 mb-2">
                    <Info className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <p className="font-bold">{t('pwResetDevTitle')}</p>
                      <p className="text-muted-foreground">{t('pwResetDevDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={devUrl} className="flex-1 text-xs text-primary underline break-all" data-testid="forgot-dev-link">{devUrl}</a>
                    <Button variant="ghost" size="icon" onClick={copyDev} data-testid="forgot-dev-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => window.location.href = devUrl} data-testid="forgot-dev-open-btn"><ExternalLink className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              )}

              <Link to="/login" className="block mt-6">
                <Button className="w-full" data-testid="forgot-goto-login-btn">{t('pwResetBackToLogin')}</Button>
              </Link>
              <button onClick={() => { setSubmitted(false); setDevUrl(null); }} className="mt-3 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-try-another-btn">
                {t('pwResetTryAnother')}
              </button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
